import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle,
  Loader2,
  CreditCard,
  DollarSign,
  Calendar,
  Clock,
  AlertCircle,
  XCircle,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BalancePaymentPage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  const success = searchParams.get("success") === "true";
  const cancelled = searchParams.get("cancelled") === "true";
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (success && sessionId) {
      verifyPayment();
    } else if (cancelled) {
      toast.error("Payment was cancelled");
      fetchPaymentInfo();
    } else {
      fetchPaymentInfo();
    }
  }, [appointmentId, success, cancelled, sessionId]);

  const fetchPaymentInfo = async () => {
    try {
      // Public endpoint - no auth needed
      const response = await axios.get(
        `${API_URL}/public/appointments/${appointmentId}/payment-info`,
      );
      setPaymentInfo(response.data);

      if (response.data.payment_status === "fully_paid") {
        setPaymentStatus("paid");
      }
    } catch (error) {
      console.error("Failed to fetch payment info:", error);
      setError(
        "Unable to load payment information. Please check the link and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.get(
        `${API_URL}/appointments/${appointmentId}/balance-payment-status?session_id=${sessionId}`,
      );

      if (response.data.status === "paid") {
        setPaymentStatus("paid");
        toast.success("Payment successful!");
      } else {
        fetchPaymentInfo();
      }
    } catch (error) {
      console.error("Payment verification failed:", error);
      fetchPaymentInfo();
    } finally {
      setProcessing(false);
      setLoading(false);
      // Clean URL
      window.history.replaceState({}, "", `/pay/${appointmentId}`);
    }
  };

  const handlePayNow = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/appointments/${appointmentId}/create-balance-payment`,
        { origin_url: window.location.origin },
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Failed to create payment session:", error);
      toast.error(
        error.response?.data?.detail ||
          "Failed to start payment. Please try again.",
      );
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Payment Link Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Payment Success Screen
  if (paymentStatus === "paid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div
          className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center"
          data-testid="payment-success"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Complete!
          </h1>
          <p className="text-gray-600 mb-6">
            Thank you for your payment. Your appointment is now fully paid.
          </p>

          {paymentInfo && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Service</span>
                  <span className="font-medium">
                    {paymentInfo.service_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total</span>
                  <span className="font-medium">
                    {formatCurrency(paymentInfo.service_price)}
                  </span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Status</span>
                  <span className="font-semibold">Fully Paid ✓</span>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-500">
            A confirmation email has been sent to your email address.
          </p>
        </div>
      </div>
    );
  }

  // Already Paid Screen
  if (paymentInfo?.payment_status === "fully_paid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Already Paid
          </h1>
          <p className="text-gray-600 mb-6">
            This appointment has already been fully paid. Thank you!
          </p>

          <div className="bg-gray-50 rounded-xl p-4 text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Service</span>
                <span className="font-medium">{paymentInfo.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Paid</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(paymentInfo.service_price)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No Balance Due
  if (!paymentInfo?.can_collect_balance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            No Payment Required
          </h1>
          <p className="text-gray-600">
            There is no outstanding balance for this appointment.
          </p>
        </div>
      </div>
    );
  }

  // Payment Required Screen
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4 py-8"
      data-testid="balance-payment-page"
    >
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Payment</h1>
          <p className="text-gray-600 mt-1">{paymentInfo.business_name}</p>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          {/* Customer Info */}
          <div className="p-6 border-b">
            <p className="text-sm text-gray-500 mb-1">Payment for</p>
            <p className="font-semibold text-lg">{paymentInfo.customer_name}</p>
            <p className="text-sm text-gray-500">
              {paymentInfo.customer_email}
            </p>
          </div>

          {/* Service Details */}
          <div className="p-6 border-b bg-gray-50">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="font-medium">{paymentInfo.service_name}</span>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Service Total</span>
                <span>{formatCurrency(paymentInfo.service_price)}</span>
              </div>

              {paymentInfo.deposit_amount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Deposit Paid</span>
                  <span>- {formatCurrency(paymentInfo.deposit_amount)}</span>
                </div>
              )}

              <div className="h-px bg-gray-200"></div>

              <div className="flex justify-between">
                <span className="font-semibold">Amount Due</span>
                <span className="text-2xl font-bold text-purple-600">
                  {formatCurrency(paymentInfo.remaining_amount)}
                </span>
              </div>
            </div>
          </div>

          {/* Pay Button */}
          <div className="p-6">
            <Button
              onClick={handlePayNow}
              disabled={processing}
              className="w-full h-14 text-lg bg-purple-600 hover:bg-purple-700"
              data-testid="pay-now-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay {formatCurrency(paymentInfo.remaining_amount)}
                </>
              )}
            </Button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Secure payment powered by Stripe. No card details stored.
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-6 text-gray-400 text-sm">
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            <span>Secure</span>
          </div>
          <div className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            <span>No hidden fees</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalancePaymentPage;
